import { useParams, Link, useHistory, useLocation } from 'react-router-dom';
import * as AppPics from 'assets/images';
import usePodcastEpisodes from 'hooks/API/GET/usePodcastEpisodes';
import useDeletePodcastEpisode from 'hooks/API/DELETE/useDeletePodcastEpisode';
import usePaginateDefaults from 'hooks/usePaginateDefaults';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';
import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import TablePaginate from 'components/Paginate';
import { notifier } from 'components/Toaster';
import useDebounce from 'hooks/useDebounce';
import { AppSwitchBox } from 'components';
import useQueryParams from 'hooks/useQueryParams';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import { ArrowsPng } from 'assets/images';
function EpisodeList() {
  const query = useQueryParams();
  const { podcastId, episodeId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const redirectBack = queryParams.get('redirect_back');

  const urlParams = new URLSearchParams(location.search);
  const initialKeyword = urlParams.get('keyword') || '';
  const initialFeatured = urlParams.get('featured') || '';
  const initialPage = parseInt(urlParams.get('page')) || 0;

  const [params, setParams] = usePaginateDefaults({ limit: 5, page: initialPage });
  const [search, handleSearch] = useState(initialKeyword);
  const [searchTerm, setSearchTerm] = useState(initialKeyword);
  const [filterFeatured, setFilterFeatured] = useState(initialFeatured);
  const [enableFilter, setEnableFilter] = useState(false);

  useEffect(() => {
    const updatedParams = new URLSearchParams(location.search);
    const newKeyword = updatedParams.get('keyword') || '';
    const newFeatured = updatedParams.get('featured') || '';
    const newPage = parseInt(updatedParams.get('page')) || 0;

    setSearchTerm(newKeyword);
    handleSearch(newKeyword);
    setFilterFeatured(newFeatured);
    setParams((prev) => ({ ...prev, page: newPage }));
  }, [location.key]);

  const searchKeyword = useDebounce(search, 1000);

  const updateURL = (newParams) => {
    const currentParams = new URLSearchParams(location.search);

    Object.entries(newParams).forEach(([key, value]) => {
      if (value && typeof value === 'string' && value.trim() !== '') {
        currentParams.set(key, value);
      } else {
        currentParams.delete(key);
      }
    });

    const newSearch = currentParams.toString();
    const newURL = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

    if (newURL !== location.pathname + location.search) {
      history.replace(newURL);
    }
  };

  useEffect(() => {
    updateURL({
      keyword: searchKeyword,
      featured: filterFeatured,
      page: params.page,
    });
  }, [searchKeyword, filterFeatured, params.page]);

  const episodeParams = useMemo(() => {
    return {
      ...params,
      keyword: searchKeyword,
      ...(filterFeatured === 'featured' && { featured: true }),
      ...(filterFeatured === 'not_featured' && { featured: false }),
    };
  }, [params, searchKeyword, filterFeatured]);

  const { data, isLoading } = usePodcastEpisodes({
    podcastId,
    configs: {
      params: episodeParams,
    },
    enabled: !!podcastId,
  });

  const { meta: { page, total } = {} } = data ?? {};
  const queryClient = useQueryClient();
  const episodes = data?.result || [];

  const currentEpisode = useMemo(() => {
    if (episodeId && episodes.length > 0) {
      return episodes.find((ep) => ep._id === episodeId);
    }
    return null;
  }, [episodeId, episodes]);

  const podcastName = useMemo(() => {
    if (episodes.length > 0) {
      return episodes[0].podcast?.name;
    }
    return null;
  }, [episodes]);

  const getBreadcrumbCurrent = () => {
    if (currentEpisode) {
      return currentEpisode.name;
    }
    return 'Episodes';
  };

  const getBreadcrumbLinks = () => {
    const baseLinks = [
      { label: 'Dashboard', to: '/dashboard' },
      { label: 'Podcast Home', to: '/podcast' },
      { label: 'Podcasts', to: `/podcast/list` },
    ];

    if (podcastName) {
      baseLinks.push({
        label: podcastName,
        to: `/podcast/${podcastId}/episodes`,
      });
    }

    return baseLinks;
  };

  const actionsConfig = {
    open: false,
    data: {},
    Header: null,
    Body: null,
    isDeleteOperation: false,
  };
  const [actions, setActions] = useState({ ...actionsConfig });

  const deleteMutation = useDeletePodcastEpisode(podcastId);

  const handleDelete = (episode) => {
    setActions({
      open: true,
      data: { id: episode._id },
      Header: 'Delete Episode',
      Body: `Are you sure you want to delete "${episode.name}"?`,
      isDeleteOperation: true,
    });
  };

  const handleDeleteConfirm = () => {
    if (!actions?.data?.id) return;
    deleteMutation.mutate(actions.data.id, {
      onSuccess: (res) => {
        notifier.success(res?.data?.message);
        queryClient.invalidateQueries(['podcast-episodes', podcastId]);
      },
      onError: (err) => {
        notifier.error(err?.response?.data?.message);
      },
    });
    setActions({ ...actionsConfig });
  };

  const performSearch = (value) => {
    handleSearch(value);
    setSearchTerm(value);
  };

  const clearAllFilters = () => {
    setFilterFeatured('');
    performSearch('');
    setParams((prev) => ({ ...prev, page: 0 }));
    history.replace(location.pathname);
  };

  // Handle back button navigation
  const handleBackNavigation = () => {
    if (redirectBack) {
      // Use the redirect_back URL if available
      history.push(decodeURIComponent(redirectBack));
    } else {
      // Fallback to podcast list route
      history.push('/podcast/list');
    }
  };

  return (
    <>
      <BasicDelete
        open={actions.open}
        closeModal={() => setActions({ ...actionsConfig })}
        Header={actions.Header}
        Body={actions.Body}
        handleConfirmation={handleDeleteConfirm}
      />
      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <button className="btn-back" onClick={handleBackNavigation}>
                <img src={AppPics.BackSvg} alt="Back" />
              </button>
            </li>
          </ul>
          <div
            className="tab__content
          ___adjust___tab___padding"
          >
            <div className="tab__content-actHead">
              <AppBreadcrumbs links={getBreadcrumbLinks()} current={getBreadcrumbCurrent()} />
            </div>
            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                <div className="tab__contentMenu">
                  <ul className="tab__contentMenu-list">
                    <li>
                      <h2 className="prime-head p-0 boldtitle text-nowrap">
                        {currentEpisode
                          ? `Episode: ${currentEpisode.name}`
                          : 'Manage Podcast Episodes'}
                      </h2>
                    </li>
                  </ul>

                  <div className="mainHeader__search">
                    <ul className="tab__contentMenu-list">
                      <li className="relative">
                        <input
                          className="mainHeader__searchInput"
                          placeholder="Search episodes..."
                          type="text"
                          value={searchTerm}
                          onChange={(event) => {
                            const value = event.target.value;
                            setSearchTerm(value);
                            if (value === '') {
                              performSearch('');
                            }
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              performSearch(searchTerm);
                            }
                          }}
                        />
                        <span
                          className="mainHeader__searchBtn"
                          onClick={() => {
                            performSearch(searchTerm);
                          }}
                        >
                          <img src={AppPics.TopMenuSearchSvg} alt="" />
                        </span>
                      </li>
                      <li className="px-4 flex items-center">
                        <button
                          type="button"
                          onClick={() => setEnableFilter(!enableFilter)}
                          className="flex items-center gap-1"
                        >
                          <img src={AppPics.TabFilterSvg} alt="List filter" />
                          <span className="viewsettingtitle">Filter</span>
                        </button>
                      </li>
                    </ul>
                  </div>
                  <div className="flex items-center gap-10">
                    {!enableFilter && !searchKeyword && (
                      <Link to={`/podcast/${podcastId}/episode/reorder`} className="mr-4">
                        <img
                          src={ArrowsPng}
                          alt="Reorder Episodes"
                          title="Reorder Episodes"
                          className="cursor-pointer mr-6"
                        />
                      </Link>
                    )}

                    <Link to={`/podcast/${podcastId}/episode/add`}>
                      <button className="btn-left-content add">New Episodes</button>
                    </Link>
                  </div>
                </div>
                <div className="tab__content-actBody">
                  {enableFilter && (
                    <div className="row mt-3">
                      <div className="col-lg-3">
                        <label className="label d-block">Filter Episodes</label>
                        <div className="radio-group">
                          <AppSwitchBox
                            inputProps={{
                              checked: filterFeatured === 'featured',
                              onChange: (e) => {
                                const isChecked = e.target.checked;
                                setParams((prev) => ({ ...prev, page: 0 }));
                                if (isChecked) {
                                  setFilterFeatured('featured');
                                  query.set('featured', 'true');
                                } else {
                                  setFilterFeatured('');
                                  query.delete('featured');
                                }
                              },
                            }}
                            labelProps={{ label: 'Show Featured Only' }}
                          />
                        </div>
                      </div>
                      <div className="col-lg-3 d-flex">
                        <button
                          type="button"
                          className="btn btn-plane p--tint"
                          style={{ alignSelf: 'end' }}
                          onClick={clearAllFilters}
                        >
                          Clear filter
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="tab__contentBody">
                    <div className="tableWrap">
                      <div className="table-responsive">
                        <table className="table">
                          <thead className="thead-light __appTheadColor">
                            <tr>
                              <th className="align-center">#</th>
                              <th className="align-center">Episode Name</th>
                              <th className="align-center">Subtitle</th>
                              <th className="align-center">Artists</th>
                              <th className="align-center">Duration</th>
                              <th className="align-center">Status</th>
                              <th className="align-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoading ? (
                              <tr>
                                <td colSpan={4} className="text-center py-4">
                                  Loading episodes...
                                </td>
                              </tr>
                            ) : episodes.length > 0 ? (
                              episodes.map((ep, idx) => (
                                <tr key={ep._id}>
                                  <td className="align-center">
                                    {params.page * params.limit + idx + 1}
                                  </td>
                                  <td className="align-center">{ep.name}</td>
                                  <td className="align-center">{ep.subtitle}</td>
                                  <td className="align-center">{ep.artists}</td>
                                  <td className="align-center">
                                    {ep?.duration === 'NaN:NaN' ? '00:00' : ep?.duration || '00:00'}
                                  </td>
                                  <td className="text-center">
                                    {ep?.isPublished ? 'Published' : 'Not Published'}
                                  </td>
                                  <td className="align-center">
                                    <Link
                                      className="actions-icons mx-2 text-primary"
                                      title="Edit"
                                      to={{
                                        pathname: `/podcast/${podcastId}/episode/${ep._id}`,
                                        search: `?redirect_back=${encodeURIComponent(
                                          location.pathname + location.search
                                        )}`,
                                      }}
                                    >
                                      <img src={AppPics.EditSvg} alt="Edit" />
                                    </Link>
                                    <button
                                      className="actions-icons mx-2 text-danger"
                                      title="Delete"
                                      onClick={() => handleDelete(ep)}
                                    >
                                      <img src={AppPics.DeleteSvg} alt="Delete" />
                                    </button>
                                    <Link
                                      to={{
                                        pathname: `/podcast/${podcastId}/episode/${ep._id}/playables`,
                                        search: `?redirect_back=${encodeURIComponent(
                                          location.pathname + location.search
                                        )}`,
                                      }}
                                      title="View Playables"
                                    >
                                      <button>
                                        <img src={AppPics.PodcastPng} alt="View Playables" />
                                      </button>
                                    </Link>
                                  </td>
                                </tr>
                              ))
                            ) : searchKeyword.trim() !== '' ? (
                              <tr>
                                <td colSpan={4} className="text-center py-4">
                                  No episodes found for "{searchKeyword}"
                                </td>
                              </tr>
                            ) : (
                              <tr>
                                <td colSpan={4} className="text-center py-4">
                                  No episodes available.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <TablePaginate
                      currentPage={page}
                      maxCount={total}
                      defaultLimit={params.limit}
                      setPage={(page) => setParams((prev) => ({ ...prev, page }))}
                      handleChange={(limit) => setParams((prev) => ({ ...prev, page: 0, limit }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EpisodeList;
